import React from 'react';
import { ReactSVG } from 'react-svg';
import {
  RawData,
  TmoCard,
  ConfirmationButton
} from '@tmobile/tmo-bps-syncup-web-component-lib';

import classNames from 'classnames';
import ItemDetailList from 'components/ItemDetailList/ItemDetailList';

import style from './DisplayRatePlanListItem.module.css';
import gridStyle from '../ItemDetailList/ItemDetailList.module.css';

import deleteIcon from '../../static/assets/icons/action/delete.svg';

/**
 * DisplayRatePlanListItem
 * -----------------------
 * Renders a single Rate Plan in a styled card format.
 *
 * Features:
 * - Shows key details (ID, Plan Name, Product).
 * - Provides an option to view raw JSON data for debugging/auditing.
 * - Allows deletion of the rate plan with a confirmation modal.
 *
 * Props:
 * - data (object): The full rate plan object to display.
 * - onDelete (function): Callback to trigger deletion, receives the plan ID.
 */
function DisplayRatePlanListItem({ data, onDelete }) {
  // Extract relevant fields from rate plan data
  const { planName, product, id } = data;

  /** Handle Delete Click */
  const handleDelete = () => onDelete(id);

  /** Renders confirmation dialog message for delete action */
  const renderDeleteMessage = name => (
    <>
      Are you sure you want to delete plan <b>{name}</b>?
    </>
  );

  return (
    <div className={style.container}>
      <TmoCard>
        {/* ====== Plan Details Section ====== */}
        <div className={style.card_container}>
          {/* Display unique identifier */}
          <DetailRow label="ID" value={id} />

          {/* Display other details as key-value pairs */}
          <ItemDetailList
            rowSet={[{ 'Plan Name': planName }, { Product: product }]}
          />
        </div>

        {/* ====== Action Section ====== */}
        <div className={style.action}>
          {/* View raw JSON data in a modal (for debugging/inspection) */}
          <RawData
            rawObject={data}
            title="Plan Details"
            type="Plan"
            usePortal={false}
            modalType="slide"
            buttonType="magenta_link"
          />

          {/* Delete button with confirmation dialog */}
          <ConfirmationButton
            className={style.button}
            buttonType="magenta_secondary"
            handleDeleteClicked={handleDelete}
            modalTitle="Delete Plan"
            modalMessage={renderDeleteMessage(planName)}
            modalAcceptButtonText="Yes, Delete"
            buttonIcon={<ReactSVG src={deleteIcon} />}
            tooltip={`Delete plan ${planName}`}
            type="delete"
            aria-label={`Delete plan ${planName}`}
          />
        </div>
      </TmoCard>
    </div>
  );
}

/**
 * DetailRow
 * ---------
 * Helper component for rendering a single label/value pair in grid format.
 *
 * Example:
 *   <DetailRow label="ID" value="12345" />
 */
function DetailRow({ label, value }) {
  return (
    <div className={classNames(gridStyle.row, style.app_id_row)}>
      <label className={gridStyle.label}>{label}</label>
      <div className={gridStyle.value}>{value}</div>
    </div>
  );
}

export default DisplayRatePlanListItem;
